import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLocationDot,
  faMoneyBill1,
  faHeart,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";

const Last_Posted_Vacancies = () => {
  const { t, i18n } = useTranslation();
  const [vacancies, setVacancies] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchVacancies = async (pageNum = 1) => {
    setLoading(true);
    try {
      const response = await fetch(
       `/api/get_all_vacancies.php?page=${pageNum}&limit=20&lang=${i18n.language}`,
        { cache: "no-store" }
      );
      const data = await response.json();
      if (data.success) {
        setVacancies(data.vacancies);
        setTotalPages(data.totalPages);
      } else {
        console.error("Ошибка при получении вакансий:", data.message);
      }
    } catch (error) {
      console.error("Ошибка сети:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVacancies(page);
  }, [page, i18n.language]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="w-full flex justify-center items-center h-[200px]">
        <p className="text-[#006674] font-semibold animate-pulse text-xl">
          {t("loading")}...
        </p>
      </div>
    );
  }

  return (
    <section className="w-full flex justify-start items-end p-[20px] flex-col gap-5">
      <h1 className="w-full max-sm:text-xl text-white font-bold uppercase text-start max-sm:pl-0 pl-6 text-4xl">
        {t("lastvacansies")}
      </h1>

      <section className="w-full flex justify-start p-[20px] flex-wrap items-center gap-5">
        {vacancies.map((vacancy) => (
          <article
            key={vacancy.id}
            className="w-[440px] overflow-hidden max-lg:w-[350px] max-md:w-full pt-3 flex flex-col gap-3 items-center rounded-lg shadow-2xl bg-white cursor-pointer relative transition-all duration-300 hover:-translate-y-1"
          >
            <div className="w-full flex gap-6 px-3 items-center">
              <img
                className="w-[100px] h-[100px] object-contain rounded-xl border-[4px] border-white shadow-lg bg-gray-300"
                src={`/api/uploads/${vacancy.companyLogo}`}
                alt={vacancy.companyName || "Company Logo"}
              />

              <ul className="flex flex-col gap-1">
                <li>
                  <Link
                    to={`/vacancy/${vacancy.id}`}
                    className="text-xl text-[#006674] font-semibold hover:underline"
                  >
                    {vacancy.workname}
                  </Link>
                </li>
                <li className="text-gray-600">
                  {vacancy.companyName || t("unknownCompany")}
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <FontAwesomeIcon
                    className="text-[#006674]"
                    icon={faLocationDot}
                  />
                  {vacancy.workplace || t("unknownLocation")}
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <FontAwesomeIcon
                    className="text-[#006674]"
                    icon={faMoneyBill1}
                  />
                  {vacancy.salary ? (
                    <span>
                      {vacancy.salary}{" "}
                      {vacancy.currency === "AMD" && <span>֏</span>}
                      {vacancy.currency === "USD" && <span>$</span>}
                      {vacancy.currency === "RUB" && <span>₽</span>}
                      {vacancy.currency === "EUR" && <span>€</span>}
                    </span>
                  ) : (
                    <span>{t("Indefinite")}</span>
                  )}
                </li>
              </ul>
            </div>

            <button className="absolute right-2 top-24 w-[35px] h-[35px] rounded-full bg-gray-400 text-white border-2 shadow-lg hover:bg-[#006674] transition">
              <FontAwesomeIcon icon={faHeart} />
            </button>

            <div className="w-full mt-2 p-2 flex justify-between items-center bg-[#006674] text-white">
              <span>
                <FontAwesomeIcon className="mr-1" icon={faCalendarAlt} />
                {new Date(vacancy.created_at).toLocaleDateString()}
              </span>
              <Link to={`/vacancy/${vacancy.id}`} className="hover:underline">
                {t("seemore")}
              </Link>
            </div>
          </article>
        ))}
      </section>

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="flex gap-3 mt-6 flex-wrap justify-center">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className={`px-4 py-2 rounded-lg border-2 text-white font-semibold ${
              page === 1
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-white hover:text-[#006674] border-white transition-all"
            }`}
          >
            ⬅ {t("previous")}
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => handlePageChange(i + 1)}
              className={`px-3 py-2 rounded-lg border-2 font-semibold ${
                page === i + 1
                  ? "bg-white text-[#006674] border-white"
                  : "border-white text-white hover:bg-white hover:text-[#006674] transition-all"
              }`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            className={`px-4 py-2 rounded-lg border-2 text-white font-semibold ${
              page === totalPages
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-white hover:text-[#006674] border-white transition-all"
            }`}
          >
            {t("next")} ➡
          </button>
        </div>
      )}
    </section>
  );
};

export default Last_Posted_Vacancies;
